import React, { useMemo, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useProfile } from '../../context/ProfileContext';
import { useMarketData } from '../../context/MarketDataContext';
import { useT } from '../../i18n/useT';
import { F } from '../../styles/theme';
import { useCurrency } from '../../context/CurrencyContext';
import { buildTaxLots, summarizeLots } from '../../utils/taxLots';
import { ALL_COINS, availableHoldingCoins, buildReleaseAgenda } from '../../utils/releaseAgenda';
import { dropScamDustTxs } from '../../utils/txView';
import { rulesFor } from '../../utils/taxRules';
import { COIN_MAP } from '../../utils/coingecko';
import { fmtC, fmtEur } from '../../utils/format';
import CoinImage from '../CoinImage';
import { CoinSelectButton, CoinPickerSheet } from './CoinSelect';

// Smart-Check-Mini-Dokument (Dashboard, nur Desktop, nur Country=DE).
// Zwei Modi über den Picker:
//   • „Alle Coins" (Default) → Portfolio-Freigabe-Agenda (Überblick).
//   • ein Coin → FIFO-Haltefrist-Status dieses Coins (unveränderter Bestand).
// Wenn keine Daten → null.
export default function SmartCheckMini({ txs, onOpenSmartCheck }) {
  const { T } = useTheme();
  const { country } = useProfile();
  const { marketData } = useMarketData();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  // Smart Check (1-Jahres-Haltefrist) ist DE-spezifisch. Länder ohne Frist-Konzept
  // (AT: Neuvermögen ohne Frist; übrige: keine Bewertung) blenden das Feature aus.
  const showSmartCheck = rulesFor(country).showSmartCheck;

  // [picked] = ALL_COINS (Überblick, Default) | ein Coin | null (→ Auto-Default).
  const [picked, setPicked] = useState(ALL_COINS);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Scam-Dust (Address-Poisoning) heraushalten — gleicher zentraler Filter wie
  // WalletScreen/AssetDetail. Einmal memoisieren → FIFO-Pool-Cache greift.
  const dustFreeTxs = useMemo(() => dropScamDustTxs(txs, marketData), [txs, marketData]);

  // Coins mit offenen Lots — zentral via availableHoldingCoins (war hier dupliziert).
  const availableCoins = useMemo(
    () => availableHoldingCoins(txs, dustFreeTxs, marketData),
    [txs, dustFreeTxs, marketData]
  );

  const isOverview = picked === ALL_COINS;

  // Größten Krypto-Bestand als Einzel-Default (defensiv: BTC bevorzugt).
  const targetSym = useMemo(() => {
    if (!txs?.length) return null;
    const hasBtc = txs.some(tx => (tx.coin === 'BTC' || tx.coinIn === 'BTC'));
    if (hasBtc) return 'BTC';
    const t0 = txs.find(tx => tx.coin || tx.coinIn);
    return t0?.coin || t0?.coinIn || null;
  }, [txs]);

  // Effektiver Einzel-Coin (nur relevant, wenn kein Überblick).
  const effectiveSym = (picked && picked !== ALL_COINS && availableCoins.includes(picked))
    ? picked
    : (availableCoins.includes(targetSym) ? targetSym : (availableCoins[0] || targetSym));

  // Überblick-Agenda (Portfolio-weit).
  const agenda = useMemo(
    () => (isOverview ? buildReleaseAgenda({ coins: availableCoins, dustFreeTxs, marketData }) : null),
    [isOverview, availableCoins, dustFreeTxs, marketData]
  );

  // Einzel-Coin-Zusammenfassung.
  const summary = useMemo(() => {
    if (isOverview || !effectiveSym) return null;
    const lots = buildTaxLots(effectiveSym, dustFreeTxs);
    if (!lots.length) return null;
    return { sym: effectiveSym, ...summarizeLots(lots) };
  }, [isOverview, effectiveSym, dustFreeTxs]);

  if (!showSmartCheck) return null;
  // Überblick nur zeigen, wenn es überhaupt bepreisten Bestand oder Ereignisse gibt.
  if (isOverview && (!agenda || (!agenda.hasPricedHoldings && agenda.eventCount === 0))) return null;
  // Einzel-Modus: wie bisher.
  if (!isOverview && (!summary || summary.total <= 0)) return null;

  const gain = T.gain || T.green;

  // Gemeinsamer Picker (mit „Alle Coins"-Option).
  const pickerSheet = pickerOpen && (
    <CoinPickerSheet
      coins={availableCoins}
      current={isOverview ? ALL_COINS : summary?.sym}
      marketData={marketData}
      allOption
      allLabel={t('red_ag_all_coins')}
      onPick={(c) => { setPicked(c); setPickerOpen(false); }}
      onClose={() => setPickerOpen(false)}
    />
  );

  // ── ÜBERBLICK: Portfolio-Freigabe-Agenda ──────────────────────────────────
  if (isOverview) {
    const freePctR = agenda.freePct != null ? Math.round(agenda.freePct) : null;
    const rows = agenda.events.slice(0, 3);

    return (
      <div style={{ position: 'relative', paddingTop: 2 }}>
        {/* Header — Titel + „Alle Coins"-Picker */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 0 14px', borderBottom: `1px solid ${T.line}`, marginBottom: 18,
        }}>
          <h3 style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', margin: 0, color: T.text }}>
            {t('smart_check')}
          </h3>
          {availableCoins.length > 0 && (
            <CoinSelectButton coin={ALL_COINS} allLabel={t('red_ag_all_coins')} marketData={marketData} onOpen={() => setPickerOpen(true)} />
          )}
        </div>

        {pickerSheet}

        {/* Hero: Portfolio-Frei-% */}
        {freePctR != null && (
          <div style={{
            fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
            fontSize: 52, fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1,
            color: gain, marginBottom: 8,
          }}>
            {freePctR}<span style={{ fontSize: 28, opacity: 0.7, marginLeft: 2 }}>%</span>
          </div>
        )}
        <div style={{ fontFamily: F.family, fontSize: 13.5, color: T.text, letterSpacing: '-0.01em', lineHeight: 1.4 }}>
          {t('red_ag_hero_caption')}
        </div>
        {agenda.hasPricedHoldings && (
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 9,
            fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
          }}>
            <span style={{ fontSize: 11, color: T.mute, fontFamily: F.family, letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 700 }}>≈</span>
            <span style={{ fontSize: 15, color: T.text, fontWeight: 700, letterSpacing: '-0.02em' }}>{fmtEur(convert(agenda.freeValueEur), locale)}</span>
            <span style={{ fontSize: 11, color: T.mute }}>{symbol} {t('red_ag_free_caption')}</span>
          </div>
        )}

        {/* Progress (wert-gewichtet) */}
        {freePctR != null && (
          <div style={{ height: 4, borderRadius: 2, background: T.line, overflow: 'hidden', margin: '16px 0 4px' }}>
            <div style={{ width: `${freePctR}%`, height: '100%', background: gain, transition: 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)' }} />
          </div>
        )}

        {/* Mini-Agenda */}
        {rows.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 2px' }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: T.mute }}>
                {t('red_ag_next_releases')}
              </span>
              <span style={{ flex: 1, height: 1, background: T.lineSoft || T.line }} />
            </div>
            {rows.map((ev) => (
              <button
                key={`${ev.sym}-${ev.freeDate}`}
                type="button"
                onClick={() => onOpenSmartCheck?.()}
                className="mo-row"
                style={{
                  display: 'flex', alignItems: 'center', gap: 11, width: '100%',
                  padding: '9px 0', background: 'transparent', border: 'none',
                  cursor: onOpenSmartCheck ? 'pointer' : 'default', textAlign: 'left',
                  color: T.text, WebkitTapHighlightColor: 'transparent',
                }}
              >
                <div style={{ width: 42, flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>{ev.days}</div>
                  <div style={{ fontSize: 8.5, fontWeight: 600, color: T.mute, marginTop: 1 }}>{t('red_ag_days_unit')}</div>
                </div>
                <span style={{ flexShrink: 0, display: 'inline-flex' }}><CoinImage sym={ev.sym} marketData={marketData} size={23} /></span>
                <div style={{ flex: 1, minWidth: 0, fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700 }}>
                  {fmtC(ev.amount)} <span style={{ color: T.sub, fontWeight: 600 }}>{ev.sym}</span>
                </div>
                {ev.hasPrice && ev.valueEur != null && (
                  <div style={{ flexShrink: 0, fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 700, color: T.text }}>
                    ≈ {fmtEur(convert(ev.valueEur), locale)} {symbol}
                  </div>
                )}
              </button>
            ))}
          </>
        )}

      </div>
    );
  }

  // ── EINZEL-COIN: FIFO-Haltefrist-Status (unverändert) ─────────────────────
  const freePct = Math.round((summary.free / summary.total) * 100);
  const coinId = COIN_MAP[summary.sym]?.id;
  const priceEur = coinId ? marketData?.[coinId]?.price : null;
  const hasPrice = typeof priceEur === 'number' && priceEur > 0;
  const freeValueEur = hasPrice ? summary.free * priceEur : null;
  const nextValueEur = hasPrice && summary.nextFreeLot ? summary.nextFreeLot.amount * priceEur : null;

  return (
    <div style={{ position: 'relative', paddingTop: 2 }}>
      {/* Header — Titel + Picker (statt nur „DE", damit „Alle Coins" erreichbar bleibt) */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 0 14px', borderBottom: `1px solid ${T.line}`, marginBottom: 18,
      }}>
        <h3 style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', margin: 0, color: T.text }}>
          {t('smart_check')}
        </h3>
        {availableCoins.length > 0 && (
          <CoinSelectButton coin={summary.sym} allLabel={t('red_ag_all_coins')} marketData={marketData} onOpen={() => setPickerOpen(true)} />
        )}
      </div>

      {pickerSheet}

      {/* Headline value */}
      <div style={{
        position: 'relative',
        fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
        fontSize: 52, fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1,
        color: gain, marginBottom: 8,
      }}>
        {freePct}<span style={{ fontSize: 28, opacity: 0.7, marginLeft: 2 }}>%</span>
      </div>

      <div style={{ fontFamily: F.family, fontSize: 13.5, color: T.text, letterSpacing: '-0.01em', lineHeight: 1.4, marginBottom: freeValueEur != null ? 10 : 18 }}>
        {t('red_sc_available_after_period', { sym: summary.sym })}
      </div>

      {freeValueEur != null && (
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 18,
          fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
        }}>
          <span style={{ fontSize: 11, color: T.mute, fontFamily: F.family, letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 700 }}>≈</span>
          <span style={{ fontSize: 15, color: T.text, fontWeight: 700, letterSpacing: '-0.02em' }}>{fmtEur(convert(freeValueEur), locale)}</span>
          <span style={{ fontSize: 11, color: T.mute }}>{symbol} {t('red_sc_today')}</span>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ height: 4, borderRadius: 2, background: T.line, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ width: `${freePct}%`, height: '100%', background: gain, transition: 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)' }} />
      </div>

      {/* Footer: nächste Tranche */}
      {summary.daysToNextFree !== null && summary.nextFreeLot && (
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          fontSize: 11.5, color: T.sub, letterSpacing: '-0.005em',
        }}>
          <span>
            {t('red_sc_next_tranche_in')}{' '}
            <span style={{ fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', color: T.text, fontWeight: 600 }}>
              {summary.daysToNextFree}
            </span>{' '}{t('red_sc_days')}
          </span>
          <span style={{ fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums', color: T.text }}>
            {fmtC(summary.nextFreeLot.amount)} {summary.sym}
            {nextValueEur != null && (
              <span style={{ color: T.mute, marginLeft: 6, fontWeight: 500 }}>
                · {fmtEur(convert(nextValueEur), locale)} {symbol}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
